/**
 * Module for communicating with Jabra headsets
 * - will use Jabra browser plugin if available and otherwise use WebHID
 */

import { useSettingsStore } from '@/stores/Settings'
import globalEvents from '@/utilities/GlobalEvents'
import * as jabraSDK from '@gnaudio/jabra-js'
import { useMediaDevicesStore } from '@/stores/MediaDevices'
import _i18n from '@/utilities/I18n'
import logger from '@/utilities/Logger'
import { MediaDeviceType, type MediaDevice } from '@/stores/MediaDevices.types'
import type { Subscription } from 'rxjs'
import { sleep } from '../Tools'
import { ref, type Ref } from 'vue'

const i18n = _i18n.global

export default class Jabra {
	static #instance: Jabra // NOSONAR

	/** Is a Jabra headset in use */
	use: Ref<boolean> = ref(false)

	useDeviceId: Ref<string | undefined> = ref(undefined)

	useDeviceName: Ref<string | undefined> = ref(undefined)

	jabraBrowserInitialized: Ref<boolean> = ref(false)

	private initErrorCount = 0

	private skipSpecificNextHeadsetEvent?: string = undefined

	private jabraApi?: jabraSDK.IApi = undefined

	private callControl?: jabraSDK.ICallControl = undefined

	private signalsSubscription?: Subscription = undefined

	private gotLock = false

	private isInitializing = false

	eventCallback: (event: 'ANSWER' | 'HANGUP' | 'REJECT' | 'HOLD' | 'UNHOLD') => void = () => { /** */ }

	private constructor() {
		logger.debug('Jabra: Constructor')

		globalEvents.on('Jabra:PresentWebHid', device => { this.PresentWebHid(device) }) // Invoked when the user has accepted to use the Jabra headset in WebHID mode

		// Triggered when media device has been changed and we need to check if the selected device is a Jabra device and thus we need to initialize the Jabra headset
		globalEvents.on('MediaDeviceChange', device => {
			const mediaDevices = useMediaDevicesStore()
			if (device.kind === MediaDeviceType.audioinput && mediaDevices.microphoneDevice?.label.toLowerCase().includes('jabra')) {
				this.init()
			} else if (this.use.value) {
				this.use.value = false
				this.useDeviceId.value = undefined
				this.useDeviceName.value = undefined
			}
		})

		globalEvents.on('MediaDeviceSelected', data => {
			const mediaDevices = useMediaDevicesStore()
			if (data.type === MediaDeviceType.audioinput && mediaDevices.microphoneDevice?.label.toLowerCase().includes('jabra')) {
				this.init()
			} else if (this.use.value) {
				this.use.value = false
				this.useDeviceId.value = undefined
				this.useDeviceName.value = undefined
			}
		})

		globalEvents.on('StatusRequest', () => {
			globalEvents.emit('StatusReport', { key: 'jabra', value: this.use.value })
			if (this.use.value) {
				globalEvents.emit('StatusReport', { key: 'jabraDevice', value: this.useDeviceName.value as string })
			}
		})
	}

	/** Get the Jabra singleton instance */
	static get instance() {
		return Jabra.#instance
	}

	/** Create the Jabra singleton instance and return it */
	static create(eventCallback: (event: 'ANSWER' | 'HANGUP' | 'REJECT' | 'HOLD' | 'UNHOLD') => void) : Jabra {
		if (!Jabra.#instance) {
			Jabra.#instance = new Jabra()
		}
		Jabra.#instance.eventCallback = eventCallback
		return Jabra.#instance
	}

	// #region Init

	/** Setup what needs to be setup to initialize the headset device */
	private async init() {
		if (this.isInitializing) {
			logger.info('Jabra: Already initializing - skipping')
			return
		}
		this.isInitializing = true
		this.use.value = false
		this.callControl = undefined
		this.gotLock = false
		logger.info('Jabra: Initializing')

		const mediaDevices = useMediaDevicesStore()

		await mediaDevices.ready()
		await sleep(1000) // Wait for the media devices to be ready

		if (mediaDevices.microphoneDevice?.label.toLowerCase().includes('jabra')) {
			await this.initBrowser(mediaDevices.microphoneDevice)
		} else {
			logger.info('Jabra: Selected audio device is not from Jabra - initalization cancelled')
		}

		this.isInitializing = false
		logger.info('Jabra: Initialization done')
	}

	/**
	 * Initialize for browser based usage
	 * @param device Audio device we want to control
	 */
	private async initBrowser(device: MediaDevice) {
		logger.info(`Jabra: InitBrowser: Initializing - looking for ${device.label} with ID ${device.productId}`)
		if (!this.jabraBrowserInitialized.value) {
			this.jabraApi = await jabraSDK.init({
				partnerKey: '0f77-948c163b-67b8-4014-8271-de2220f89736',
				appId: 'dk_telecomx_communicator',
				appName: 'Communicator',
				transport: jabraSDK.RequestedBrowserTransport.CHROME_EXTENSION_WITH_WEB_HID_FALLBACK// ,
				// tracking: 'track-all',
				// logger: {
				// 	write(logEvent) {
				// 		logger.debug(`Jabra: SDK: ${logEvent.level}: ${logEvent.message}`);
				// 	}
				// }
			})
			if (this.jabraApi.transportContext == jabraSDK.TransportContext.CHROME_EXTENSION) {
				logger.info('Jabra: InitBrowser: Using Chrome extension for transport')
			} else {
				logger.info('Jabra: InitBrowser: Using WEB-HID for transport')
			}
		}
		this.jabraBrowserInitialized.value = true

		const hwDevice = await this.browserLookupDevice(device.productId)
		const ss = useSettingsStore()

		if (!hwDevice && this.jabraApi?.transportContext == jabraSDK.TransportContext.WEB_HID && !ss.runtime.isEmbedded) {
			// Fallback to WEB HID and no headset found - ask for permission
			logger.info('Jabra: InitBrowser: Fallback to WEB HID and no headset found - asking for permission to access HID device')
			globalEvents.emit('CommonQuestionModal', { header: i18n.t('jabra.webhidHeader'), message: i18n.t('jabra.webhidMessage'), ok: i18n.t('jabra.showDialog'),
				emit: 'Jabra:PresentWebHid', emitData: device })
		} else {
			await this.initBrowserStep2(device, hwDevice)
		}
	}

	/**
	 * Finish browser based initialization
	 * @param device Audio device we want to control
	 * @param hwDevice Jabra device we want to control
	 */
	private async initBrowserStep2(device: MediaDevice, hwDevice?: jabraSDK.IDevice) {
		if (hwDevice && this.jabraApi) {
			logger.info(`Jabra: InitBrowser: Found HW device as ${hwDevice.name} with ID ${hwDevice.browserLabel}`)
			const ccf = new jabraSDK.CallControlFactory(this.jabraApi)

			if (!ccf.supportsCallControl(hwDevice)) {
				logger.warning('Jabra:initBrowser: Device does not support call control')
				globalEvents.emit('CommonErrorModal', { header: i18n.t('jabra.webhidHeader'), message: i18n.t('jabra.deviceDoesNotSupportCallControl') })
				return
			}

			this.callControl = await ccf.createCallControl(hwDevice)
			if (!this.callControl) {
				logger.warning('Jabra: InitBrowser: Device does not support call control')
				globalEvents.emit('CommonErrorModal', { header: i18n.t('jabra.webhidHeader'), message: i18n.t('jabra.deviceDoesNotSupportCallControl') })
				return
			}

			this.use.value = true
			this.useDeviceId.value = hwDevice.browserLabel
			this.useDeviceName.value = hwDevice.name
			this.initErrorCount = 0

			try {
				const gotLock = await this.callControl.takeCallLock()

				if (gotLock) {
					try { this.callControl.ring(false) } // If we lost control during ringing, we need to stop the ringer when get back control
					catch (err) { logger.warning(`Jabra: Failed to set ring to false - ${(err as Error).message}`) }
					this.callControl.offHook(false)
					this.callControl.releaseCallLock()
				}
			}
			catch (_) { /** Ignore */ }
		} else {
			this.initErrorCount++
			if (this.initErrorCount < 10) {
				setTimeout(() => { this.init() }, 2000)
			} else {
				logger.warning(`Jabra: InitBrowser: Did not find device ${device.label}`)
				globalEvents.emit('CommonErrorModal', { header: i18n.t('jabra.errorHeader'), message: i18n.t('jabra.errorInitFailedDeviceNotFound').replace('%%name%%', device.label) })
			}
		}
	}

	/**
	 * Invoked by initBrowser when the device was not found and we are running in HID mode (not jabra integration mode) and needs the user to permit access to the device
	 * @param device The audio device we wish to find the HID device for
	 */
	private async PresentWebHid(device: MediaDevice) {
		logger.info('Jabra: Presenting WEB HID paring dialog')
		await jabraSDK.webHidPairing()
		const hwDevice = await this.browserLookupDevice(device.productId)

		this.initBrowserStep2(device, hwDevice)
	}

	/**
	 * Lookup the Jabra device we wish to control and return it
	 * @param id Id of device to lookup
	 */
	private async browserLookupDevice(id: string) {
		logger.info(`Jabra: LookupDevice: Looking for device ${id}`)
		const started = new Date()
		let hwDevice: jabraSDK.IDevice | undefined = undefined
		let deviceList: jabraSDK.IDevice[] = []

		const subscription = this.jabraApi?.deviceList.subscribe(devices => {
			if (!id && devices.length > 0) {
				hwDevice = devices[0]
			} else {
				hwDevice = devices.find(o => o.browserLabel === id)
			}
			deviceList = devices
		})

		while (!hwDevice && (new Date().getTime() - started.getTime()) < 5000) {
			await sleep(250)
		}

		if (hwDevice && (hwDevice as jabraSDK.IDevice).type === jabraSDK.DeviceType.DONGLE) {
			while (deviceList.find(device => device.type === jabraSDK.DeviceType.HEADSET && device.name.toLowerCase().includes('evolve') && RegExp(/(65|75|85)/).test(device.name)) == null &&
				new Date().getTime() - started.getTime() < 5000) {
				await sleep(250)
			}
		}

		if (subscription) {
			subscription.unsubscribe()
		}

		logger.debug('Jabra: Connected devices:')

		if (this.jabraApi) {
			const ccf = new jabraSDK.CallControlFactory(this.jabraApi)

			for (const dev of deviceList) {
				const cc = ccf.supportsCallControl(dev)

				logger.debug(`Jabra: Found device: ${dev.name} of type ${dev.type} (${jabraSDK.DeviceType[dev.type]}) - ${dev.browserLabel} - CallControl=${cc}`)
			}

			if (hwDevice && (hwDevice as jabraSDK.IDevice).type === jabraSDK.DeviceType.DONGLE) {
				// Device type is a USB bluetooth dongle, looking for the headset behind the dongle
				logger.info('Jabra: Selected device is a USB bluetooth dongle - now looking for the headset behind the dongle')
				hwDevice = undefined

				// Lookup for Evolve device of type headset with version 65, 75, 85 (because they are all bluetooth devices)
				let hsDevice = deviceList.find(device => device.type === jabraSDK.DeviceType.HEADSET && device.name.toLowerCase().includes('evolve') && RegExp(/(65|75|85)/).test(device.name))

				if (!hsDevice) { hsDevice = deviceList.find(device => device.type == jabraSDK.DeviceType.HEADSET) } // Just find any headset device

				if (hsDevice) {
					hwDevice = hsDevice
					logger.info(`Jabra: Selected hedset behind USB dongle: ${hsDevice.name}`)
				}
			}
		}

		return hwDevice
	}

	// #endregion

	// #region Event handling

	/** Start listening to headset event - can only be invoked when headset is ringing or off-hook */
	private startListenToEvents() {
		if (!this.signalsSubscription) {
			this.skipSpecificNextHeadsetEvent = undefined // Just to be sure
			logger.debug('Jabra: Starting to listening to headset events - because headset is ringing or off-hook')

			this.signalsSubscription = this.callControl?.deviceSignals.subscribe(signal => {
				logger.debug(`Jabra: Event: ${jabraSDK.SignalType[signal.type]} - val=${signal.value}`)

				switch (signal.type) {
					case jabraSDK.SignalType.HOOK_SWITCH:
						if (signal.value) {
							/** If we send a callControl event to the Jabra SDK for onHook(false), we get that event here, as if the event was triggered by the headset.
							 * So far, we've only seen that it happens for this specific event, and not for onHook(true) etc. We then set this flag, so that we know we should ignore
							 * the given event.
							 */
							if (this.skipSpecificNextHeadsetEvent === 'OFF_HOOK') {
								logger.info('Jabra: User pressed answer - we are skipping the event, because it was triggered by ourselves')
								this.skipSpecificNextHeadsetEvent = undefined
								return
							}
							logger.info('Jabra: User pressed answer')
							this.eventCallback('ANSWER')
						} else {
							logger.info('Jabra: User pressed hangup')
							this.eventCallback('HANGUP')
						}
						break

					case jabraSDK.SignalType.FLASH: // Resume call
						logger.info('Jabra: User pressed resume - resuming call on hold')
						this.eventCallback('ANSWER')
						break

					case jabraSDK.SignalType.REJECT_CALL:
						logger.info('Jabra: User pressed reject')
						this.ringOff()
						this.eventCallback('REJECT')
						break

					default:
						// If we have received another event than the one we were supposed to skip, we probably are not going to receive the event - just remove it.
						this.skipSpecificNextHeadsetEvent = undefined
						break
				}
			})
		}
	}

	/** Stop listening to events */
	private stopListenToEvents() {
		if (this.signalsSubscription) {
			logger.debug('Jabra: Stopped listening to headset events - because headset is idle')
			this.signalsSubscription.unsubscribe()
			this.signalsSubscription = undefined
			this.skipSpecificNextHeadsetEvent = undefined // Just to be sure
		}
	}

	// #endregion

	// #region Actions - public

	/** Take headset off-hook (ensure not ringing, open audio channels and start listening to events) */
	async offHook() {
		logger.info('Jabra: Taking headset off-hook')
		if (this.callControl) {
			if (!this.gotLock) { this.gotLock = await this.callControl.takeCallLock() }
			if (this.gotLock) {
				try { this.callControl.ring(false) }
				catch (err) {
					logger.warning(`Jabra: Failed to set ring to false - ${(err as Error).message}`)
				}
				this.skipSpecificNextHeadsetEvent = 'OFF_HOOK'
				this.callControl.offHook(true)
				this.startListenToEvents()
			}
		}
	}

	/** Put headset on-hook (ensure not ringing, audio channels are closed and stop listening to events) */
	onHook() {
		logger.info('Jabra: Putting headset on-hook')
		if (this.callControl && this.gotLock) {
			this.callControl.offHook(false)

			try { this.callControl.ring(false) }
			catch (err) {
				logger.warning(`Jabra: Failed to set ring to false - ${(err as Error).message}`)
			}
			this.stopListenToEvents()
			this.callControl.releaseCallLock()
			this.gotLock = false
			logger.info('Jabra: Done putting headset on-hook - stopped listening to events and released call lock')
		} else {
			logger.info(`Jabra: Done putting headset on-hook - did not stop listening to events nor released call lock, gotLock is ${this.gotLock}`)
		}
	}

	/** Start ringing on the headset (and listening to events) */
	async ringOn() {
		logger.info('Jabra: Starting ringer')
		if (this.callControl) {
			if (!this.gotLock) { this.gotLock = await this.callControl.takeCallLock() }
			if (this.gotLock) {
				try { this.callControl.ring(true) }
				catch (err) {
					logger.warning(`Jabra: Failed to set ring to true - ${(err as Error).message}`)
				}
				this.startListenToEvents()
			}
		}
	}

	/** Stop ringing on the headset (and stop listening to events) */
	ringOff() {
		logger.info('Jabra: Stopping ringer')
		if (this.callControl && this.gotLock) {
			try { this.callControl.ring(false) }
			catch (err) {
				logger.warning(`Jabra: Failed to set ring to false - ${(err as Error).message}`)
			}
			this.stopListenToEvents()
			this.callControl.releaseCallLock()
			this.gotLock = false
		}
	}

	/** Put headset on hold */
	hold() {
		logger.info('Jabra: Putting headset on hold')
		if (this.callControl && this.gotLock) {
			try { this.callControl.hold(true) }
			catch (err) {
				logger.warning(`Jabra: Failed to set hold to true - ${(err as Error).message}`)
			}
		}
	}

	/** Resume held call on headset */
	unhold() {
		logger.info('Jabra: Putting headset off hold')
		if (this.callControl && this.gotLock) {
			try { this.callControl.hold(false) }
			catch (err) {
				logger.warning(`Jabra: Failed to set hold to false - ${(err as Error).message}`)
			}
		}
	}

	// #endregion
}
